import Head from 'next/head'
import Link from 'next/link'
import Image from 'next/image'

import { useRouter } from 'next/router'
import { useContext, useEffect, useState } from 'react'

import { trackEvent } from '@/analytics'

import Banner from '@/components/hire/banner'
import Steps from '@/components/hire/steps'
import Dashboard from '@/components/hire/dashboard'
import Query from '@/components/hire/query'
import Services from '@/components/hire/services'
import Footer from '@/components/hire/footer'
import { isMobile } from '@/lib/utils'

const Header = dynamic(() => import('@/components/hire/main-nav'))
const Faq = dynamic(() => import('@/components/hire/faq'))
const Testimonial = dynamic(() => import('@/components/hire/testimonial'))

import { ADVERTISER_CATEGORIES, ADVERTISER_LOCATIONS } from '../../constants/all'

import {
  multipleGetCalls,
  sortListBykeyInObject,
  advertiserAnalyticOptions
} from '../../utils/functions'
import Cookies from 'universal-cookie'
import { pageScripts } from '../_app'

import Candidate from '../../../public/hire/candidate.webp'
import Employer from '../../../public/hire/employer.webp'
import { Icons } from '../../components/hire/icons'
import { SharedContext } from '../../context/SharedContext'
import { AdvertiserContext } from '../../context/AdvertiserContext'
import dynamic from 'next/dynamic'
import { useRedirectIfAuth } from '../../hooks/useAuth'

const AdvertiserHomePage = ({ topCategories, categories, locations }) => {
  const { utmParams, setUtmParams } = useContext(SharedContext)

  const { enterpriseLoginDetails } = useContext(AdvertiserContext)

  const router = useRouter()
  const [hydrated, setHydrated] = useState(false)
  const [showpopup, setshowpopup] = useState(() => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search)
      const noPopup = searchParams.get('no_popup')
      if (noPopup === '1' || noPopup === 'true') {
        return false
      }
    }
    return true
  })
  const [formpopup, setformpopup] = useState(false)
  const query = window.location.search
  const urlParam = new URLSearchParams(query)

  useRedirectIfAuth()

  //Below useEffect just confirming the rendered page has become interactive
  useEffect(() => {
    setHydrated(true)
  }, [])

  useEffect(() => {
    if (showpopup) {
      document.body.classList.add('no-scroll')
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
      document.activeElement.blur()
    } else {
      document.body.classList.remove('no-scroll')
      document.body.style.overflow = 'auto'
      document.documentElement.style.overflow = ''
    }
    return () => {
      document.body.classList.remove('no-scroll')
      document.body.style.overflow = 'auto'
      document.documentElement.style.overflow = ''
    }
  }, [showpopup])

  //Below useEffect tracking the event of viewing landing page if router object is ready
  useEffect(() => {
    if (router.isReady) {
      trackEvent({
        name: 'viewed_landing_page',
        searchParams: utmParams || {},
        options: {
          ...advertiserAnalyticOptions({
            enterprise: !!enterpriseLoginDetails?.advertiserId,
            category: 'impression'
          }),
          device_type: isMobile() ? 'mobile' : 'web',
          label: 'landing_page'
        }
      })
      setUtmParams(router?.query || {})
    }
  }, [router.isReady])

  if (!hydrated) {
    return pageScripts()
  }

  function onScroll() {
    window.scrollTo({
      top: 10,
      behavior: 'smooth'
    })
  }

  return (
    <div className="bg-white">
      <Head>
        <title>Post Your Job| Hire Employees | Lokal Employer Login</title>
        <meta name="title" content="Post Your Job| Hire Employees | Lokal Employer Login" />
        <meta
          name="description"
          content="Hire from 2500000+ pool of skilled candidates. Browse and filter from your district, recruit within 24hrs.Post a Job Vacancy : Telecallers, Admin office Staff & more."
        />
        <meta
          property="og:image"
          content={`${process.env.NEXT_PUBLIC_APP_URL}assets/recruiter_open_graph.jpg`}
        />
        <meta
          property="og:description"
          content="Hire from 2500000+ pool of skilled candidates. Browse and filter from your district, recruit within 24hrs.Post a Job Vacancy : Telecallers, Admin office Staff & more."
        />
        <meta property="og:title" content="Post Your Job| Hire Employees | Lokal Employer Login" />
        <meta
          property="twitter:description"
          content="Hire from 2500000+ pool of skilled candidates. Browse and filter from your district, recruit within 24hrs.Post a Job Vacancy : Telecallers, Admin office Staff & more."
        />
        <meta
          property="twitter:title"
          content="Post Your Job| Hire Employees | Lokal Employer Login"
        />
      </Head>
      {pageScripts()}
      <div className="h-[56px] md:h-[80px]">
        <Header showPopup={showpopup} formpopup={formpopup} handleScroll={onScroll} />
      </div>

      <div className={'md:space-y-[100px] lg:space-y-[150px]'}>
        <Banner utm={urlParam} />
        <Steps />
        <Dashboard formpopup={formpopup} setformpopup={setformpopup} />
        <Services categories={categories} locations={locations} />
        <Query />
        <Testimonial />
        <Faq />
      </div>
      <Footer jobs={topCategories} locations={locations} />

      {/* First popup open after 4sec landing on the website */}
      <div className={`flex justify-center`}>
        {showpopup && (
          <div
            data-state={`${showpopup ? 'open' : 'closed'}`}
            className={`fixed inset-0 z-[9999] bg-[#595959]/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0`}
            style={{ pointerEvents: 'auto' }}
            data-aria-hidden="true"
            aria-hidden="true"
          />
        )}
        {showpopup && (
          <div
            style={{ pointerEvents: 'auto' }}
            data-state={`${showpopup ? 'open' : 'closed'}`}
            className={`${
              isMobile()
                ? 'shadow-lg fixed inset-x-0 bottom-0 z-[999999] h-[100%] min-w-[100%] gap-4 bg-white p-[24px]'
                : 'shadow-lg fixed inset-y-0 top-[60px] z-[999999] flex h-[95vh] max-w-sm items-center justify-center gap-4 rounded-[16px] bg-popUp py-[36px] px-[80px] md:top-[20px] md:min-w-[60%]'
            }`}
            tabIndex="-1"
          >
            <div className="space-x-[8px] text-center">
              <div className={'flex items-center gap-x-[36px] pb-[9px] md:hidden'}>
                <Link
                  href={''}
                  as={'/hire'}
                  className={'flex cursor-pointer items-center justify-center space-x-[12px]'}
                >
                  <Icons.logo />
                </Link>
              </div>
              <h2
                className={
                  ' custom-text2 notoSansBold p-[16px] text-[18px] leading-[28px] md:px-[24px] md:py-[12px] lg:text-[24px] lg:leading-[32px]'
                }
              >
                #No.1 Job platform for Bharat users
              </h2>
              <h1
                className={
                  'notoSansBold w-full px-[40px] text-[22px] leading-[30px] text-title md:text-[30px] md:leading-[49px] lg:text-[36px]'
                }
              >
                {`Choose what are you looking for`}
              </h1>
              <div
                className={
                  'mt-[32px] flex flex-col flex-wrap items-center justify-center gap-y-[12px] md:flex-row md:flex-nowrap md:justify-between md:gap-y-[0px] md:gap-x-[80px]'
                }
              >
                <div
                  className="flex w-[100%] items-center rounded-[8px] border-[1px] border-solid border-greyDivider200 p-[16px] hover:bg-greyDisabledBackground50  md:flex-col"
                  onClick={() => {
                    trackEvent({
                      name: 'tap_hire',
                      searchParams: utmParams || {},
                      options: {
                        ...advertiserAnalyticOptions({
                          enterprise: !!enterpriseLoginDetails?.advertiserId
                        }),
                        label: 'landing_page',
                        section: 'pop_up',
                        device_type: isMobile() ? 'mobile' : 'web'
                      }
                    })
                    setshowpopup(false)
                  }}
                >
                  <input
                    type="radio"
                    className="hidden scale-[1.2] self-end text-right md:flex"
                    onClick={() => {
                      trackEvent({
                        name: 'tap_hire',
                        searchParams: utmParams || {},
                        options: {
                          ...advertiserAnalyticOptions({
                            enterprise: !!enterpriseLoginDetails?.advertiserId
                          }),
                          label: 'landing_page',
                          section: 'pop_up',
                          device_type: isMobile() ? 'mobile' : 'web'
                        }
                      })
                      setshowpopup(false)
                    }}
                  />
                  <Image
                    src={Employer}
                    className={
                      'md:custom-border-employer mx-[auto] h-[105px] w-[105px] -scale-x-100 rounded-[8px] object-cover object-right md:h-[194px] md:w-[194px] md:rounded-[9999px]'
                    }
                    width={120}
                    height={120}
                    alt={''}
                  />
                  <p
                    className={
                      'mx-[auto] mt-[8px] mb-[16px] text-[16px] font-bold leading-[24px] text-[#323232] md:mb-[18px] md:text-[20px] md:leading-[28px]'
                    }
                  >
                    I want to Hire
                  </p>
                  <input
                    type="radio"
                    className="mb-[5px] scale-[1.2] cursor-pointer rounded-[50%] md:hidden"
                    onClick={() => {
                      setshowpopup(false)
                      trackEvent({
                        name: 'tap_hire',
                        searchParams: utmParams || {},
                        options: {
                          ...advertiserAnalyticOptions({
                            enterprise: !!enterpriseLoginDetails?.advertiserId
                          }),
                          label: 'landing_page',
                          section: 'pop_up',
                          device_type: isMobile() ? 'mobile' : 'web'
                        }
                      })
                    }}
                  />
                </div>
                <div
                  className="flex w-[100%] items-center rounded-[8px] border-[1px] border-solid border-greyDivider200 p-[16px] hover:bg-greyDisabledBackground50 md:flex-col"
                  onClick={() => {
                    trackEvent({
                      name: 'tap_seeker',
                      searchParams: utmParams || {},
                      options: {
                        ...advertiserAnalyticOptions({
                          enterprise: !!enterpriseLoginDetails?.advertiserId
                        }),
                        label: 'landing_page',
                        section: 'pop_up',
                        device_type: isMobile() ? 'mobile' : 'web'
                      }
                    })
                    window.open('/jobs/jobs-near-me')
                  }}
                >
                  <input
                    type="radio"
                    className="hidden scale-[1.2] self-end text-right md:flex"
                    onClick={() => {
                      trackEvent({
                        name: 'tap_seeker',
                        searchParams: utmParams || {},
                        options: {
                          ...advertiserAnalyticOptions({
                            enterprise: !!enterpriseLoginDetails?.advertiserId
                          }),
                          label: 'landing_page',
                          section: 'pop_up',
                          device_type: isMobile() ? 'mobile' : 'web'
                        }
                      })
                      window.open('/jobs/jobs-near-me')
                    }}
                    checked={false}
                  />
                  <Image
                    src={Candidate}
                    className={
                      'md:custom-border-candidate mx-[auto] h-[105px] w-[105px] -scale-x-100 rounded-[8px] border-[10px] object-cover object-center md:h-[194px] md:w-[194px] md:rounded-[9999px]'
                    }
                    width={120}
                    height={120}
                    alt={''}
                  />
                  <p
                    className={
                      'mx-[auto] mt-[8px] mb-[16px] text-[16px] font-bold leading-[24px] text-[#323232] md:mb-[18px] md:text-[20px] md:leading-[28px]'
                    }
                  >
                    I want a Job
                  </p>
                  <label>
                    <input
                      type="radio"
                      className="scale-[1.2] md:hidden"
                      onClick={() => {
                        window.open('/jobs/jobs-near-me')
                      }}
                      checked={false}
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export const getStaticProps = async () => {
  const cookies = new Cookies()
  const language = cookies.get('language')
  const apiDetails = [
    {
      url: process.env.NEXT_PUBLIC_BASE_URL_ADS + ADVERTISER_CATEGORIES,
      params: { only_top: 1 },
      header: { 'Accept-Language': language },
      isTokenPresent: false
    },
    {
      url: process.env.NEXT_PUBLIC_BASE_URL_ADS + ADVERTISER_CATEGORIES,
      params: { only_top: 1 },
      header: { 'Accept-Language': language },
      isTokenPresent: false
    },
    {
      url: process.env.NEXT_PUBLIC_BASE_URL_ADS + ADVERTISER_LOCATIONS,
      params: { only_top: 1 },
      header: { 'Accept-Language': language },
      isTokenPresent: false
    }
  ]

  const res = await multipleGetCalls(apiDetails)

  return {
    props: {
      categories: res?.[0],
      topCategories: sortListBykeyInObject({ list: res?.[1], key: 'title' }),
      locations: sortListBykeyInObject({ list: res?.[2]?.results, key: 'title' })
    }
  }
}

export default AdvertiserHomePage
